/**
 * Email Thread Data Model
 * Defines the structure for email thread data received from services
 */

export class EmailAttachment {
  constructor(data) {
    this.fileName = data.fileName || data.name || data.file_name || '';
    this.mimeType = data.mimeType || data.mime_type || '';
    this.size = data.size || data.size_in_bytes || data.file_size || 0;
    this.url  = data.url || ""
  }

  /**
   * Format file size to human-readable format
   */
  getFormattedSize() {
    const bytes = this.size;
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  }

  /**
   * Get file extension from filename
   */
  getFileExtension() {
    return this.fileName.split('.').pop().toUpperCase();
  }
}

export class EmailMessage {
  constructor(data) {
    this.sequence = typeof data.sequence === 'number' ? data.sequence : (data.version || 0);
    this.from = data.from || data.from_email || '';
    this.senderName = data.senderName || data.sender_name || '';
    // Normalize customerName: prefer explicit customerName field, otherwise fall back
    // to senderName, then derive a friendly name from the email address if possible.
    const deriveNameFromEmail = (email) => {
      if (!email || typeof email !== 'string') return '';
      // handle formats like "Name <email@domain.com>"
      const angleMatch = email.match(/^(.*)\s*<([^>]+)>\s*$/);
      if (angleMatch) {
        const namePart = angleMatch[1].trim();
        if (namePart) return namePart;
        email = angleMatch[2];
      }
      // use local-part of the email
      const local = email.split('@')[0] || '';
      // replace separators with spaces and capitalize words
      const words = local.replace(/[._\-+]/g, ' ').split(' ').filter(Boolean);
      const cap = words.map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
      return cap;
    };

    this.customerName = data.customerName || data.customer_name || this.senderName || deriveNameFromEmail(this.from) || '';
    this.to = data.to || [];
    this.sentAt = data.sentAt || data.sent_at || data.createdAt || data.created_at || '';
    this.type = data.type || 'OUTBOUND';
    this.body = data.body || data.body_content || data.preview || '';
    this.attachments = (data.attachments || data.attachment_info || []).map(att => new EmailAttachment(att));
  }

  /**
   * Check if message is outbound (from customer)
   */
  isOutbound() {
    return this.type === 'OUTBOUND';
  }

  /**
   * Check if message is inbound (from insurance company)
   */
  isInbound() {
    return this.type === 'INBOUND';
  }

  /**
   * Format sent date to readable format
   */
  getFormattedDate() {
    const date = new Date(this.sentAt);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  /**
   * Get time only
   */
  getFormattedTime() {
    const date = new Date(this.sentAt);
    return date.toLocaleString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  /**
   * Check if message has attachments
   */
  hasAttachments() {
    return this.attachments && this.attachments.length > 0;
  }
}

export class EmailThread {
  constructor(data) {
    this.threadId = data.threadId || data.emailThreadId || data.email_thread_id || (data.id ? String(data.id) : '');
    this.subject = data.subject || '';
    this.status = data.status || 'IN_PROGRESS';
    this.createdAt = data.createdAt || data.created_at || '';
    this.updatedAt = data.updatedAt || data.updated_at || '';
    this.messages = (data.messages || []).map(msg => new EmailMessage(msg));
  }

  /**
   * Get the most recent message
   */
  getLatestMessage() {
    if (this.messages.length === 0) return null;
    return this.messages[this.messages.length - 1];
  }

  /**
   * Get formatted last updated time
   */
  getFormattedUpdatedAt() {
    const date = new Date(this.updatedAt);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  /**
   * Get all messages sorted by sequence
   */
  getSortedMessages() {
    return [...this.messages].sort((a, b) => a.sequence - b.sequence);
  }

  /**
   * Get total attachment count across all messages
   */
  getTotalAttachmentCount() {
    return this.messages.reduce((count, msg) => count + msg.attachments.length, 0);
  }
}

/**
 * Sample data for testing
 */
export const sampleEmailThreadData = {
  "threadId": "CLM-402",
  "subject": "Request to Initiate Insurance Claim – Recent Hospitalisation",
  "status": "IN_PROGRESS",
  "createdAt": "2025-01-01T09:15:00Z",
  "updatedAt": "2025-01-01T11:30:00Z",
  "messages": [
    {
      "sequence": 1,
      "from": "nm_hoang_987@gmail.com",
      "senderName": "Nguyen Minh Hoang",
      "to": ["insurance@company.com"],
      "sentAt": "2025-01-01T09:15:00Z",
      "type": "OUTBOUND",
      "body": "Dear Team,\n\nI am writing to check whether I am currently eligible to receive benefits for my insurance claim (Claim ID: CLM-402).\n\nThe claim relates to my recent hospitalisation for acute myocardial infarction (heart attack), which was admitted under the Critical Illness Rider in my policy.\n\nI would appreciate it if you could confirm whether the claim meets the eligibility criteria at this stage and advise if there are any conditions or timelines that apply.\n\nThank you for your assistance.\n\nKind regards,\nNguyen Minh Hoang",
      "attachments": [
        {
          "fileName": "hospital_invoice.pdf",
          "mimeType": "application/pdf",
          "size": 245678
        },
        {
          "fileName": "claim_form.pdf",
          "mimeType": "application/pdf",
          "size": 189234
        },
        {
          "fileName": "id_proof.pdf",
          "mimeType": "application/pdf",
          "size": 98765
        }
      ]
    },
    {
      "sequence": 2,
      "from": "insurance@company.com",
      "senderName": "Sarah - Insurance Claims Representative",
      "to": ["nm_hoang_987@gmail.com"],
      "sentAt": "2025-01-01T11:30:00Z",
      "type": "INBOUND",
      "body": "Dear Nguyen Minh Hoang,\n\nThank you for your inquiry regarding Claim ID: CLM-402.\n\nAfter reviewing your claim against the terms and conditions of your policy, we would like to inform you that the claim is not currently eligible for benefit payout.\n\nReason: A 90-day waiting period applies before benefits can be admitted for this coverage. As this waiting period has not yet been completed, the claim cannot be processed at this time.\n\nOnce the waiting period requirement is met, eligibility can be reassessed in line with policy provisions.\n\nWe understand this may be disappointing and appreciate your understanding. Please feel free to reach out if you have any questions or require further clarification.\n\nKind regards,\nSarah\nInsurance Claims Representative",
      "attachments": []
    }
  ]
};
